import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { updateJavaPendingStatus, updatePhpPendingStatus } from '../api/client';
import { REJECT_ONLY_WARNING } from './PendingStatusModal';

// Bulk "update status" for the Transaction Pending page (2026-08-20) - the
// same action PendingStatusModal.jsx does for a single row, applied to a
// checkbox selection instead. Kept as its own component rather than a mode
// flag on PendingStatusModal: that one's whole body is a field-by-field dump
// of ONE record, which has no meaning for a selection, and its
// canSubmit/identifier logic is per-record. Only the form half (pick
// paid/rejected -> conditionally require error_message) is shared, and it's
// small enough that duplicating it beats contorting both.
//
// Deliberately client-side and sequential: there is no bulk endpoint, and
// adding one would mean redeploying Lumen + the BFF + API Gateway for what
// is a loop. More importantly the per-update OAuth token
// (P2MDashboard::getUpdateStatusAccessToken()) is fetched fresh and
// uncached every call by design - instapay-direct's tokens are one-time-use
// - so each row already costs two upstream round trips. Firing a selection
// of those off concurrently with Promise.all would burst the token endpoint
// for no gain; same reasoning as ExhaustedList's retrySelected().
//
// Status values are the literal lowercase paid/rejected forwarded as-is to
// instapay-direct / instapay-router (see PendingStatusModal's docblock).

// Row identity for selection + progress display. Java and PHP pending rows
// come from entirely separate systems with their own identifier naming
// (merchant_order_id <-> client_reference_no, instruction_id <->
// reference_no), so there's no single shared field - hence the source
// switch. camelCase/id fallbacks on the Java side mirror
// PendingStatusModal's javaIdentifier, whose Redis record casing was never
// confirmed against a live cluster.
export function pendingRowKey(source, row) {
  if (!row || typeof row !== 'object') {
    return null;
  }
  if (source === 'php') {
    return row.reference_no || null;
  }
  return row.merchant_order_id || row.merchantOrderId || row.id || null;
}

// Whether a row carries everything its endpoint requires. A row that fails
// this can't be submitted, so the page disables its checkbox rather than
// letting it into a selection and failing mid-loop.
export function isPendingRowUpdatable(source, row) {
  if (!row || typeof row !== 'object') {
    return false;
  }
  if (source === 'php') {
    // reference_no + client_reference_no are both required by
    // P2MDashboard::updatePhpPendingStatus(); is_merchant is required too
    // but is always present on a pendingPhp() row (it's in the SELECT), so
    // only a genuinely malformed row would miss it.
    return Boolean(row.reference_no && row.client_reference_no);
  }
  return Boolean(pendingRowKey('java', row));
}

// "Awaiting Bancnet response" rows (Transaction Pending card, and Lookup's
// router_txn result) (2026-10-10) arrive shaped by
// P2MDashboard::shapeTxnRow(): normalized fields on top, the raw p2m:txn
// record under `record`. The update modals (built for Java Pending) read
// merchant_order_id / instruction_id off whatever object they are given and
// list every field, so they get the raw record with the normalized
// identifiers laid over it.
export function awaitingModalRecord(row) {
  const rec = row?.record && typeof row.record === 'object' ? row.record : {};
  // Router p2m:txn fields as-is, Leg 3 (ips:instr) fields prefixed so the
  // two records can't overwrite each other in the detail list.
  const leg3 = Object.fromEntries(Object.entries(rec.ips_instr || {}).map(([k, v]) => [`leg3_${k}`, v]));
  return {
    ...(rec.p2m_txn || {}),
    ...leg3,
    merchant_order_id: row.merchant_order_id,
    instruction_id: row.instruction_id ?? null,
    amount: row.amount ?? null,
    merchant_id: row.merchant_id ?? null,
    merchant_name: row.merchant_name ?? null,
  };
}

const SOURCE_LABEL = { java: 'Java Pending', php: 'PHP Pending' };

// rejectOnly: status locked to "rejected" with the code prefilled as DS24 -
// for deposits whose Leg 3 never reached the router, where "paid" is never valid.
export default function BulkPendingStatusModal({ show, source, records, onClose, onSuccess, label, submitJava, rejectOnly, paidOnly }) {
  const { email } = useAuth();

  const [status, setStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (show && paidOnly && !rejectOnly) {
      setStatus('paid');
    }
    if (show && rejectOnly) {
      setStatus('rejected');
      setErrorMessage((m) => m || 'DS24');
    }
  }, [show, rejectOnly, paidOnly]);
  const [running, setRunning] = useState(false);
  const [formError, setFormError] = useState(null);
  // key -> { state: 'pending' | 'done' | 'error', message?: string }
  const [results, setResults] = useState({});
  const [finished, setFinished] = useState(false);

  const rows = useMemo(() => (Array.isArray(records) ? records : []), [records]);

  const counts = useMemo(() => {
    const values = Object.values(results);
    return {
      done: values.filter((r) => r.state === 'done').length,
      failed: values.filter((r) => r.state === 'error').length,
      settled: values.filter((r) => r.state !== 'pending').length,
    };
  }, [results]);

  if (!show || rows.length === 0) {
    return null;
  }

  function reset() {
    setStatus('');
    setErrorMessage('');
    setRunning(false);
    setFormError(null);
    setResults({});
    setFinished(false);
  }

  function handleClose() {
    if (running) {
      return;
    }
    reset();
    onClose();
  }

  async function updateOne(row) {
    const key = pendingRowKey(source, row);
    if (source === 'php') {
      await updatePhpPendingStatus({
        referenceNo: row.reference_no,
        clientReferenceNo: row.client_reference_no,
        status,
        errorMessage: status === 'rejected' ? errorMessage.trim() : null,
        updatedBy: email,
        isMerchant: row.is_merchant,
      });
    } else {
      await (submitJava || updateJavaPendingStatus)({
        merchantOrderId: key,
        instructionId: row.instruction_id || row.instructionId || null,
        status,
        errorMessage: status === 'rejected' ? errorMessage.trim() : null,
        updatedBy: email,
      });
    }
  }

  // One pass over `targets`, sequential on purpose (see the docblock). Each
  // success is reported up immediately rather than batched at the end, so
  // rows disappear from the table as they land and a mid-run failure still
  // leaves the page's state accurate for everything already done.
  async function run(targets) {
    setRunning(true);
    setFinished(false);

    for (const row of targets) {
      const key = pendingRowKey(source, row);
      // eslint-disable-next-line no-loop-func
      setResults((prev) => ({ ...prev, [key]: { state: 'pending' } }));

      if (!isPendingRowUpdatable(source, row)) {
        // eslint-disable-next-line no-loop-func
        setResults((prev) => ({
          ...prev,
          [key]: { state: 'error', message: 'Row is missing a required identifier field.' },
        }));
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        await updateOne(row);
        // eslint-disable-next-line no-loop-func
        setResults((prev) => ({ ...prev, [key]: { state: 'done' } }));
        onSuccess?.(row, status);
      } catch (err) {
        // eslint-disable-next-line no-loop-func
        setResults((prev) => ({
          ...prev,
          [key]: { state: 'error', message: err.message || 'Update failed.' },
        }));
      }
    }

    setRunning(false);
    setFinished(true);
  }

  function handleSubmit(e) {
    e?.preventDefault?.();
    setFormError(null);

    if (!status) {
      setFormError('Choose a status.');
      return;
    }
    if (status === 'rejected' && errorMessage.trim() === '') {
      setFormError('error_message is required when status is rejected.');
      return;
    }

    run(rows);
  }

  // Only the rows that errored, and only those still present in the current
  // selection - anything that succeeded is already gone from the table.
  function handleRetryFailed() {
    const failed = rows.filter((row) => results[pendingRowKey(source, row)]?.state === 'error');
    if (failed.length > 0) {
      run(failed);
    }
  }

  const started = counts.settled > 0 || running;
  const progressPct = rows.length > 0 ? Math.round((counts.settled / rows.length) * 100) : 0;

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose} />
      <div className="modal show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0">
            <div className="modal-header">
              <h1 className="modal-title h5">
                Bulk update status{' '}
                <span className="text-muted small">
                  ({label || SOURCE_LABEL[source] || source} — {rows.length} selected)
                </span>
              </h1>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} disabled={running} />
            </div>

            <div className="modal-body">
              {!started && (
                <div className="alert alert-info py-2 small">
                  The status and error message below are applied to <strong>all {rows.length}</strong> selected
                  transaction{rows.length === 1 ? '' : 's'}. They are submitted one at a time and cannot be undone
                  from here.
                </div>
              )}
              {!started && rejectOnly && (
                <div className="alert alert-warning py-2 small">{REJECT_ONLY_WARNING}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label small">New status</label>
                    <select
                      className="form-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={running || started}
                      required
                    >
                      {!rejectOnly && !paidOnly && <option value="">Choose…</option>}
                      {!rejectOnly && <option value="paid">paid</option>}
                      {(rejectOnly || !paidOnly) && <option value="rejected">rejected</option>}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small">Error message</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={errorMessage}
                      onChange={(e) => setErrorMessage(e.target.value)}
                      disabled={running || started || status !== 'rejected'}
                      required={status === 'rejected'}
                      placeholder={
                        status === 'rejected'
                          ? 'Reason for rejection — applied to every selected row…'
                          : 'Only used when status is rejected'
                      }
                    />
                  </div>
                </div>

                {formError && <p className="text-danger small mb-3">{formError}</p>}
              </form>

              {started && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="small text-muted">
                      {running
                        ? `Updating ${Math.min(counts.settled + 1, rows.length)} of ${rows.length}…`
                        : `Finished — ${counts.done} updated${counts.failed > 0 ? `, ${counts.failed} failed` : ''}.`}
                    </span>
                    <span className="small text-muted">{progressPct}%</span>
                  </div>
                  <div className="progress mb-3" style={{ height: '0.4rem' }}>
                    <div
                      className={`progress-bar ${counts.failed > 0 ? 'bg-warning' : 'bg-success'}`}
                      role="progressbar"
                      style={{ width: `${progressPct}%` }}
                      aria-valuenow={progressPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </>
              )}

              <div className="table-responsive" style={{ maxHeight: '18rem' }}>
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{source === 'php' ? 'Reference no' : 'Merchant order ID'}</th>
                      <th>{source === 'php' ? 'Client reference no' : 'Instruction ID'}</th>
                      <th style={{ width: '9rem' }}>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const key = pendingRowKey(source, row);
                      const result = results[key];
                      const secondary =
                        source === 'php'
                          ? row.client_reference_no
                          : row.instruction_id || row.instructionId;
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={key || i}>
                          <td className="small text-break">{key || '—'}</td>
                          <td className="small text-break">{secondary || '—'}</td>
                          <td>
                            {!result && <span className="text-muted small">Not started</span>}
                            {result?.state === 'pending' && (
                              <span className="d-inline-flex align-items-center gap-1 text-muted small">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Updating…
                              </span>
                            )}
                            {result?.state === 'done' && <span className="badge text-bg-success">updated</span>}
                            {result?.state === 'error' && (
                              <span className="badge text-bg-danger" title={result.message}>
                                failed
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {finished && counts.failed > 0 && (
                <div className="alert alert-warning py-2 small mt-3 mb-0">
                  {counts.failed} row{counts.failed === 1 ? '' : 's'} failed and {counts.failed === 1 ? 'is' : 'are'}{' '}
                  still pending. Hover a <span className="badge text-bg-danger">failed</span> badge for the reason, or
                  retry just those rows.
                </div>
              )}
            </div>

            <div className="modal-footer">
              {finished ? (
                <>
                  {counts.failed > 0 && (
                    <button type="button" className="btn btn-outline-primary" onClick={handleRetryFailed}>
                      Retry failed ({counts.failed})
                    </button>
                  )}
                  <button type="button" className="btn btn-primary" onClick={handleClose}>
                    Done
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={running}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={running}>
                    {running ? 'Updating…' : `Update ${rows.length} transaction${rows.length === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
